import { defineStore } from 'pinia'
import Bugsnag from '@bugsnag/js'

import { getAccessToken } from '@/api/idp'
import AppService from '@/services/appService'
import type { Portal } from '@/types/portal'
import type { User } from '@/types/user'
import type { EmbedUser } from '@/types/embedUser'
import type { BusinessProfile } from '@/types/businessProfile'
import type { DesignFlow } from '@/types/designFlow'
import designFlows from '@/constants/designFlows'
import Clarity from '@microsoft/clarity'
import { v4 as uuidv4 } from 'uuid'

interface AppState {
  portal: Portal | null
  user: User | null
  businessProfiles: BusinessProfile[]
  selectedBusinessProfileId: number | null
  selectedBusinessProfileLogo: string
  stepperCount: number
  selectedDesignFlow: DesignFlow,
  embedUser: EmbedUser | null,
  sessionId: string,
  profileCurrencySymbol: string,
  TraceSessionId: string,
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    portal: null,
    user: null,
    businessProfiles: [],
    selectedBusinessProfileId: null,
    selectedBusinessProfileLogo: '',
    stepperCount: 0,
    selectedDesignFlow: designFlows.generate,
    embedUser: null,
    sessionId: uuidv4(),
    profileCurrencySymbol: '₹',
    TraceSessionId: '',
  }),
  getters: {
    getPortalDomain(): string {
      return window.location.hostname.split(/\./).slice(-2).join('.')
    },
    getPortalId(): number | null {
      return this.portal?.id || null
    },
    getTraceSessionId(): string {
      return this.TraceSessionId
    }
  },
  actions: {
    setPortal(portal: Portal | null): void {
      this.portal = portal
    },
    setUser(user: User | null): void {
      this.user = user
      if (import.meta.env.VITE_ENV === 'testing') {
        if (user?.id) {
          Clarity.identify(user.id.toString(), '', '', user?.fullName)
        }
      }
    },
    setEmbedUser(embedUser: EmbedUser | null) {
      this.embedUser = embedUser
    },
    setBusinessProfiles(businessProfiles: BusinessProfile[]): void {
      this.businessProfiles = businessProfiles
    },
    setSelectedBusinessProfileId(selectedBusinessProfileId: number | null): void {
      this.selectedBusinessProfileId = selectedBusinessProfileId
    },
    setSelectedBusinessProfileLogo(selectedBusinessProfileLogo: string): void {
      this.selectedBusinessProfileLogo = selectedBusinessProfileLogo
    },
    incrementStepperCount(): void {
      this.stepperCount++
    },
    setStepperCount(stepperCount: number): void {
      this.stepperCount = stepperCount
    },
    setDesignFlow(designFlow: DesignFlow): void {
      this.selectedDesignFlow = designFlow
    },
    setProfileCurrency(currency: string): void {
      this.profileCurrencySymbol = currency
    },
    setTraceSessionId(): void {
      this.TraceSessionId = uuidv4()
    },
    async hydrateUserFromToken(): Promise<User | null> {
      if (this.user) {
        return this.user
      }

      if (!getAccessToken()) {
        return null
      }

      const appService = new AppService()
      const user = await appService.getCurrentUser()
      user.isSignedIn = true
      this.setUser(user)
      Bugsnag.setUser(String(user.id), user.emailAddress, `${user.firstName} ${user.lastName}`)
      return user
    },
  },
})
